import React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import Input from "@/components/ui/input";
import Text from "@/components/ui/text";
import { monetaryValueFormatter } from "@/pages/deals/helpers";
import { reducePrecision } from "@/utils/numbers";
import { MetricRow } from "./types";
import { parseInputToNumber, isModified } from "./utils";

const TRANSLATION_PREFIX = "contracts.contractView.financialRecognitionModal";

interface Props {
  rows: MetricRow[];
  recognized: Record<string, string>;
  setRecognized: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  currencySymbol: string;
  onResetRow: (row: MetricRow) => void;
}

const MetricsTable: React.FC<Props> = ({
  rows,
  recognized,
  setRecognized,
  currencySymbol,
  onResetRow
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col">
      <div className="grid grid-cols-[1fr,1fr,1fr,5rem] gap-x-4 px-2 py-2 border-b border-border-border1">
        <Text variant="bodySecondary">
          {t(`${TRANSLATION_PREFIX}.metricColumn`)}
        </Text>
        <Text variant="bodySecondary">
          {t(`${TRANSLATION_PREFIX}.calculatedColumn`)}
        </Text>
        <Text variant="bodySecondary">
          {t(`${TRANSLATION_PREFIX}.recognizedColumn`)}
        </Text>
        <span />
      </div>
      {rows.map((row) => {
        const calculatedDisplay =
          row.calculatedValue != null
            ? monetaryValueFormatter(
                reducePrecision(row.calculatedValue, 0),
                currencySymbol
              )
            : "—";
        const recognizedNum = parseInputToNumber(recognized[row.key]);
        const modified = isModified(recognizedNum, row.calculatedValue);
        return (
          <div
            key={row.key}
            className="grid grid-cols-[1fr,1fr,1fr,5rem] gap-x-4 items-center px-2 py-2 border-b border-border-border1"
          >
            <Text variant="body">{row.label}</Text>
            <Text variant="body">{calculatedDisplay}</Text>
            <Input
              value={recognized[row.key]}
              onChange={(e) =>
                setRecognized((prev) => ({
                  ...prev,
                  [row.key]: e.target.value
                }))
              }
              leadingIcon={
                <Text variant="body" className="text-secondary">
                  {currencySymbol}
                </Text>
              }
              inputMode="decimal"
              data-test={`fin-rec-input-${row.key}`}
            />
            {modified ? (
              <Button
                variant="tertiary"
                onClick={() => onResetRow(row)}
                data-test={`fin-rec-reset-${row.key}`}
              >
                {t(`${TRANSLATION_PREFIX}.reset`)}
              </Button>
            ) : (
              <span />
            )}
          </div>
        );
      })}
    </div>
  );
};

export default MetricsTable;
